import React, { useRef, useEffect, useState, memo } from "react";
import { useDrag } from "react-use-gesture";
import { useThree } from "@react-three/fiber";
import { ToCoord3D } from "../shape/Intersect";



/**
 * A draggable 3D point component.
 * @param {string} uuid - The unique identifier of the point.
 * @param {Object} vpoint - The initial position of the point as a Vector3 object.
 * @param {string} color - The color of the point.
 * @param {number} size - The size of the point.
 * @param {function} callEnd - A callback function that is called when the drag ends.
 * @param {Object} mesh - The mesh object that the point is attached to.
 * @returns {JSX.Element} A mesh component representing the draggable 3D point.
 */
export const PointDrag3D = memo(
  ({ uuid, vpoint, color, size, callEnd, mesh }) => {
    const [node, setNode] = useState([]);
    const ref = useRef();
    const { mouse, camera } = useThree();

    useEffect(() => {
      setNode(vpoint);
    }, [vpoint]);

    useEffect(() => {
      if(ref.current){
        ref.current.position.set(node.x, node.y, node.z);
      }
    }, [node]);

    const bindDrag = useDrag(
      ({ down, event }) => {
        if (event) event.stopPropagation();
        var vpt = ToCoord3D(mesh, camera, mouse);

        if (vpt) {
          setNode(vpt);
          if (down === false && callEnd) callEnd(uuid, vpt);
        }
      },
      { pointerEvents: true }
    );

    return (
      <mesh ref={ref} {...bindDrag()} renderOrder={1}>
        <sphereGeometry attach="geometry" args={[size, 16, 16]} />
        <meshBasicMaterial attach="material" color={color} depthTest={true} depthWrite={true} />
      </mesh>
    );
  }
);


/**
 * Renders draggable 3D points on a mesh.
 * @param {Array[THREE.Vector3]} vpoints - Array of 3D points to be rendered.
 * @param {string[]} idx - Array of unique identifiers for each point.
 * @param {string} color - Color of the points.
 * @param {number} size - Size of the points.
 * @param {function} callEnd - Function to be called when dragging ends.
 * @param {Object} mesh - Mesh on which the points are rendered.
 * @returns {JSX.Element} - React component that renders the draggable 3D points.
 */
export const PointsDrag3D = memo(({ vpoints, idx, color, size, callEnd, mesh }) => {
    var objects = [];
    vpoints.forEach((v, i) => {
      objects.push(
        <PointDrag3D
          key={idx[i]}
          uuid={idx[i]}
          vpoint={v}
          color={color}
          size={size}
          callEnd={callEnd}
          mesh={mesh}
        />
      );
    });

    return objects.length !== 0 ? (
      <React.Fragment>{objects}</React.Fragment>
    ) : null;  }
);
